/**
 * Streaming Markdown Split
 *
 * Splits partial/streaming markdown into "safe" (complete constructs only)
 * and "pending" (incomplete tail). Use safe for MarkdownContent and pending
 * as plain text so we don't render half-closed tags during stream.
 */

export interface StreamingMarkdownSplit {
  safe: string;
  pending: string;
}

/**
 * Find the index where the "incomplete" tail starts.
 * Returns content.length if the whole string is complete.
 */
function findPendingStart(content: string): number {
  if (!content.trim()) return content.length;

  let pendingStart = content.length;

  // 1. Code block: unclosed ``` (fenced)
  const codeBlockRe = /```/g;
  let match;
  const codeBlockPositions: number[] = [];
  while ((match = codeBlockRe.exec(content)) !== null) {
    codeBlockPositions.push(match.index);
  }
  if (codeBlockPositions.length % 2 === 1) {
    // Last ``` has no closer
    pendingStart = Math.min(pendingStart, codeBlockPositions[codeBlockPositions.length - 1]!);
  }

  // 2. Inline backticks: odd count (outside of code blocks only)
  const codeBlockRanges = pairRanges(codeBlockPositions);
  const outsideCodeBlocks = excludeRanges(
    content,
    codeBlockRanges
  );
  const backtickCount = (outsideCodeBlocks.match(/`/g) || []).length;
  if (backtickCount % 2 === 1) {
    const lastBacktick = findLastIndexOutsideRanges(
      content,
      (c) => c === '`',
      codeBlockRanges
    );
    if (lastBacktick >= 0) pendingStart = Math.min(pendingStart, lastBacktick);
  }

  // 3. Bold ** : unclosed (odd number of **)
  const boldDoubleRe = /\*\*/g;
  const boldDoublePositions: number[] = [];
  while ((match = boldDoubleRe.exec(content)) !== null) {
    boldDoublePositions.push(match.index);
  }
  if (boldDoublePositions.length % 2 === 1) {
    pendingStart = Math.min(
      pendingStart,
      boldDoublePositions[boldDoublePositions.length - 1]!
    );
  }

  // 4. Bold __ : unclosed
  const boldUnderRe = /__/g;
  const boldUnderPositions: number[] = [];
  while ((match = boldUnderRe.exec(content)) !== null) {
    boldUnderPositions.push(match.index);
  }
  if (boldUnderPositions.length % 2 === 1) {
    pendingStart = Math.min(
      pendingStart,
      boldUnderPositions[boldUnderPositions.length - 1]!
    );
  }

  // 5. Italic * (single, not part of **): odd count
  const singleStarMatches = content.match(/(?<!\*)\*(?!\*)/g);
  if (singleStarMatches && singleStarMatches.length % 2 === 1) {
    let lastSingleStar = -1;
    for (let i = 0; i < content.length; i++) {
      if (
        content[i] === '*' &&
        content[i + 1] !== '*' &&
        content[i - 1] !== '*'
      ) {
        lastSingleStar = i;
      }
    }
    if (lastSingleStar >= 0) pendingStart = Math.min(pendingStart, lastSingleStar);
  }

  // 6. Link [ without ]( : last [ that isn't followed by ]( before end
  const lastBracket = content.lastIndexOf('[');
  if (lastBracket >= 0) {
    const afterBracket = content.slice(lastBracket);
    if (!afterBracket.includes('](')) {
      pendingStart = Math.min(pendingStart, lastBracket);
    }
  }

  return pendingStart;
}

/** Pair indices into [start, end] ranges; end is start of closing ```, so add 3 to include it */
function pairRanges(indices: number[]): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (let i = 0; i < indices.length - 1; i += 2) {
    const closeStart = indices[i + 1]!;
    ranges.push([indices[i]!, closeStart + 3]); // include closing ```
  }
  return ranges;
}

function isInsideRanges(index: number, ranges: Array<[number, number]>): boolean {
  return ranges.some(([start, end]) => index >= start && index < end);
}

/** Find last index where predicate matches, excluding positions inside ranges */
function findLastIndexOutsideRanges(
  content: string,
  predicate: (char: string) => boolean,
  ranges: Array<[number, number]>
): number {
  for (let i = content.length - 1; i >= 0; i--) {
    if (predicate(content[i]!) && !isInsideRanges(i, ranges)) return i;
  }
  return -1;
}

/** Return content with given ranges removed (replaced with spaces to preserve length) */
function excludeRanges(content: string, ranges: Array<[number, number]>): string {
  const arr = content.split('');
  for (const [start, end] of ranges) {
    for (let i = start; i < end; i++) arr[i] = ' ';
  }
  return arr.join('');
}

/**
 * Split streaming markdown into safe (complete) and pending (incomplete) parts.
 * Safe content ends at the last complete line before any unclosed construct,
 * so we never render partial lines or half-closed tags.
 * Render safe with MarkdownContent and pending as plain text.
 */
export function getStreamingMarkdownSplit(content: string): StreamingMarkdownSplit {
  if (!content) return { safe: '', pending: '' };

  const tagBoundary = findPendingStart(content);

  // When we have incomplete content, also use line breaks: only flush safe
  // content at newlines so we never render a partial line (avoids mid-line flicker).
  let safeEnd = tagBoundary;
  if (tagBoundary < content.length && tagBoundary > 0) {
    const lastNewlineBeforeBoundary = content.lastIndexOf('\n', tagBoundary - 1);
    if (lastNewlineBeforeBoundary >= 0) {
      safeEnd = lastNewlineBeforeBoundary + 1;
    }
  }

  return {
    safe: content.slice(0, safeEnd),
    pending: content.slice(safeEnd),
  };
}
